import clsx from 'clsx'
import { Dropdown, DropdownItem, Icon } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from 'components/Filters/FilterPanel/subcomponents/Dropdown/Dropdown.module.scss'
import FilterComponents from 'components/Filters/FilterPanel/subcomponents'

const ROW_SELECTOR = '.dropdown-row, .dropdown-item'
const INTERACTIVE_SELECTOR =
  'a[href], button:not([disabled]), [role="menuitem"], [role="button"], input:not([disabled]), [data-dropdown-action]'
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const isRowDisabled = (el) =>
  el.getAttribute('aria-disabled') === 'true' || el.hasAttribute('disabled')

const FilterDropdown = ({
  filterManager,
  dropdownFilters = [],
  dropdownClassName = '',
  nestedDropdownClassName = '',
  keyboardNavigation = true,
  onKeyDown: onKeyDownProp,
}) => {
  const { t } = useTranslation()
  const [visibleFilter, setVisibleFilter] = useState(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const containerRef = useRef(null)
  const nestedRef = useRef(null)

  const { updateFilter, filters, initial } = filterManager

  const showFilter = useCallback(
    (value) => {
      if (visibleFilter !== value) {
        setVisibleFilter(value)
      }
    },
    [visibleFilter, setVisibleFilter],
  )

  const getRows = useCallback(() => {
    const root = containerRef.current
    if (!root) return []
    return Array.from(root.querySelectorAll(ROW_SELECTOR))
  }, [])

  const selectRow = useCallback(
    (index) => {
      setActiveIndex(index)
      const filter = dropdownFilters[index]
      if (filter) showFilter(filter.key)
    },
    [dropdownFilters, showFilter],
  )

  const moveActive = useCallback(
    (direction) => {
      const rows = getRows()
      if (!rows.length) return
      let next = activeIndex
      for (let step = 0; step < rows.length; step++) {
        next = (next + direction + rows.length) % rows.length
        if (!isRowDisabled(rows[next])) {
          selectRow(next)
          return
        }
      }
    },
    [activeIndex, getRows, selectRow],
  )

  const jumpTo = useCallback(
    (target) => {
      const rows = getRows()
      if (!rows.length) return
      const step = target === 0 ? 1 : -1
      let i = target
      while (i >= 0 && i < rows.length && isRowDisabled(rows[i])) i += step
      if (i < 0 || i >= rows.length) return
      selectRow(i)
    },
    [getRows, selectRow],
  )

  // When opened (e.g. via keyboard), focus the container and auto-select the
  // first enabled row so the highlight + nested sub-container populate without
  // requiring a hover.
  useEffect(() => {
    if (!keyboardNavigation) return
    const root = containerRef.current
    if (!root) return
    if (typeof root.focus === 'function') root.focus({ preventScroll: true })
    const rows = getRows()
    const firstEnabled = rows.findIndex((row) => !isRowDisabled(row))
    if (firstEnabled >= 0) selectRow(firstEnabled)
  }, [])

  const focusNested = useCallback(() => {
    const root = nestedRef.current
    if (!root) return false
    const target = root.querySelector(FOCUSABLE_SELECTOR)
    if (target && typeof target.focus === 'function') {
      target.focus()
      return true
    }
    return false
  }, [])

  const activateRow = useCallback(() => {
    const rows = getRows()
    const row = rows[activeIndex]
    if (!row || isRowDisabled(row)) return
    const target = row.querySelector(INTERACTIVE_SELECTOR) || row
    target.click()
  }, [activeIndex, getRows])

  const handleKeyDown = useCallback(
    (event) => {
      if (keyboardNavigation) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault()
            moveActive(1)
            break
          case 'ArrowUp':
            event.preventDefault()
            moveActive(-1)
            break
          case 'Home':
            event.preventDefault()
            jumpTo(0)
            break
          case 'End':
            event.preventDefault()
            jumpTo(getRows().length - 1)
            break
          case 'Enter':
            event.preventDefault()
            activateRow()
            break
          case 'ArrowRight':
          case 'Tab':
            if (event.key === 'Tab' && event.shiftKey) break
            if (focusNested()) {
              event.preventDefault()
            }
            break
          default:
            break
        }
      }
      if (onKeyDownProp) onKeyDownProp(event)
    },
    [
      keyboardNavigation,
      moveActive,
      jumpTo,
      activateRow,
      focusNested,
      getRows,
      onKeyDownProp,
    ],
  )

  const handleRowMouseOver = (index) => {
    selectRow(index)
  }

  const handleMouseLeave = () => {
    setActiveIndex(-1)
  }

  return (
    <>
      <div
        ref={containerRef}
        tabIndex={keyboardNavigation ? 0 : undefined}
        role={keyboardNavigation ? 'menu' : undefined}
        className={styles['dropdown-container']}
        onKeyDown={handleKeyDown}
        onMouseLeave={handleMouseLeave}
      >
        <Dropdown className={clsx(styles['dropdown'], dropdownClassName)}>
          {dropdownFilters.map((filter, index) => {
            return (
              <div
                key={'filter-row' + filter.key}
                className={clsx('dropdown-row', styles['dropdown-node'], {
                  [styles['dropdown-node_opened']]:
                    visibleFilter === filter.key,
                  [styles['dropdown-row_focused']]: activeIndex === index,
                })}
                aria-disabled={filter.disabled || undefined}
                onMouseOver={() => handleRowMouseOver(index)}
              >
                <DropdownItem
                  endIcon={
                    <Icon
                      name={'arrow-right'}
                      size={'s'}
                      color={'Label-Fifth'}
                    />
                  }
                >
                  {t(filter.label)}
                </DropdownItem>
              </div>
            )
          })}
        </Dropdown>
      </div>
      <div ref={nestedRef}>
        <Dropdown className={clsx(styles['dropdown'], dropdownClassName)}>
          {dropdownFilters.map((filter) => {
            if (visibleFilter !== filter.key) return null

            const El = FilterComponents[filter.type]

            return (
              <El
                key={'filter-dropdown' + filter.key}
                className={clsx(styles['dropdown'], nestedDropdownClassName, {
                  [styles['dropdown_opened']]: visibleFilter === filter.key,
                })}
                {...filter}
                defaultValue={initial[filter.key]}
                value={filters[filter.key]}
                name={filter.key}
                filterManager={filterManager}
                onApply={(update) => {
                  setVisibleFilter(null)
                  updateFilter(update)
                }}
              />
            )
          })}
        </Dropdown>
      </div>
    </>
  )
}

FilterDropdown.propTypes = {
  filterManager: PropTypes.object,
  dropdownFilters: PropTypes.array,
  title: PropTypes.string,
  dropdownClassName: PropTypes.string,
  nestedDropdownClassName: PropTypes.string,
  keyboardNavigation: PropTypes.bool,
  onKeyDown: PropTypes.func,
}

export default FilterDropdown
